/**
 * CSS Variable Bridge
 * 
 * Reads Tailwind CSS v4 @theme custom properties at runtime.
 * Use for canvas rendering, chart libraries (Recharts), and dynamic inline styles
 * where Tailwind classes cannot be applied.
 * 
 * SSOT: All color values come from @theme in src/index.css.
 * Never hardcode colors here -- this module only reads CSS variables.
 */

let _root: CSSStyleDeclaration | null = null;

function getRoot(): CSSStyleDeclaration {
  if (!_root) _root = getComputedStyle(document.documentElement);
  return _root;
}

export function getCSSVar(name: string): string {
  return getRoot().getPropertyValue(name).trim();
}

/** Invalidate cached root (call if theme changes dynamically) */
export function invalidateCache(): void {
  _root = null;
}

/**
 * Convenience accessor for common theme colors.
 * Uses getters so values are always fresh from CSS.
 */
export const themeColor = {
  get background() { return getCSSVar('--color-background'); },
  get surface() { return getCSSVar('--color-surface'); },
  get surfaceHover() { return getCSSVar('--color-surface-hover'); },
  get surfaceActive() { return getCSSVar('--color-surface-active'); },

  get border() { return getCSSVar('--color-border'); },
  get borderLight() { return getCSSVar('--color-border-light'); },
  /** Opaque 6-digit separator: safe to append an alpha suffix (canvas strokes). */
  get separator() { return getCSSVar('--color-separator'); },

  get primary() { return getCSSVar('--color-primary'); },
  get primaryDim() { return getCSSVar('--color-primary-dim'); },
  get primaryGlow() { return getCSSVar('--color-primary-glow'); },

  get warning() { return getCSSVar('--color-warning'); },
  get warningDim() { return getCSSVar('--color-warning-dim'); },
  get warningGlow() { return getCSSVar('--color-warning-glow'); },

  get danger() { return getCSSVar('--color-danger'); },
  get dangerDim() { return getCSSVar('--color-danger-dim'); },
  get dangerGlow() { return getCSSVar('--color-danger-glow'); },

  get info() { return getCSSVar('--color-info'); },
  get infoDim() { return getCSSVar('--color-info-dim'); },

  get success() { return getCSSVar('--color-success'); },
  get successDim() { return getCSSVar('--color-success-dim'); },

  get text() { return getCSSVar('--color-text'); },
  get textMuted() { return getCSSVar('--color-text-muted'); },
  get textDim() { return getCSSVar('--color-text-dim'); },

  get orange() { return getCSSVar('--color-orange'); },
  get cyan() { return getCSSVar('--color-cyan'); },
  get indigo() { return getCSSVar('--color-indigo'); },
  get purpleLight() { return getCSSVar('--color-purple-light'); },
  get lime() { return getCSSVar('--color-lime'); },
  get emerald() { return getCSSVar('--color-emerald'); },

  /** Climate metric colours (Elevia metric tokens), independent of the status colours. */
  get metricTemperature() { return getCSSVar('--color-metric-temperature'); },
  get metricHumidity() { return getCSSVar('--color-metric-humidity'); },
  get metricCo2() { return getCSSVar('--color-metric-co2'); },
  get metricVpd() { return getCSSVar('--color-metric-vpd'); },
};

/**
 * Canvas font in the UI family (font.ui, the `--font-sans` theme stack): canvas text does not
 * inherit CSS font-family. Example: canvasFont('11px') or canvasFont('14px', 'bold').
 */
export function canvasFont(size: string, weight?: string): string {
  const family = getCSSVar('--font-sans') || 'sans-serif';
  return `${weight ? `${weight} ` : ''}${size} ${family}`;
}

/** Get room accent color by index (cycles through 5 colors) */
export function getRoomColor(index: number): string {
  const keys = ['room1', 'room2', 'room3', 'room4', 'room5'];
  return getCSSVar(`--color-${keys[index % keys.length]}`);
}

/**
 * Get status color string for canvas/chart contexts.
 * Returns the resolved hex/rgba color value.
 */
export function getStatusColor(status: 'healthy' | 'warning' | 'critical' | 'off'): string {
  switch (status) {
    case 'healthy': return themeColor.primary;
    case 'warning': return themeColor.warning;
    case 'critical': return themeColor.danger;
    case 'off':
    default: return themeColor.textDim;
  }
}
